import { GlobalWithArgs } from './_types';

/**
 * Simple one-liner typeguard for validing and casting a string as a key into an object.
 * @param {object} x The object to evaluate against.
 * @param {string} k The key to check for
 * @returns {k is keyof T} True if the provided value is a valid key into {@link x}.
 */
export const isKey = <T extends object>(x: T, k: PropertyKey): k is keyof T => k in x;

/**
 * Extracts all function property keys from a given object.
 *
 * @template T - The type of the source object, must extend object
 * @param {T} source - The source object to extract function keys from
 * @returns {Array<keyof T>} An array containing the keys of all properties that are functions
 *
 * @remarks
 * This utility iterates through all enumerable properties of an object and identifies
 * which ones are functions, returning an array of their keys. Useful for introspection,
 * creating method proxies, or filtering callable properties.
 *
 * @example
 * ```typescript
 * const myObject = {
 *   name: 'John',
 *   age: 30,
 *   greet: () => console.log('Hello'),
 *   calculate: (x: number) => x * 2
 * };
 *
 * const functionKeys = extractFunctions(myObject);
 * // Returns: ['greet', 'calculate']
 * ```
 *
 * @example
 * ```typescript
 * // Use case: Creating a method wrapper
 * class MyService {
 *   data = 'some data';
 *   fetchData() { return this.data; }
 *   saveData(val: string) { this.data = val; }
 * }
 *
 * const service = new MyService();
 * const methods = extractFunctions(service);
 * // Returns: ['fetchData', 'saveData']
 * ```
 */
export const extractFunctions = <T extends object>(source: T): Array<keyof T> => {
  return Array.from(Object.entries(source)).reduce((arr, [key, check]) => {
    if (typeof check === 'function') {
      arr.push(key as keyof T);
    }
    return arr;
  }, [] as Array<keyof T>);
};

/**
 * Retrieves the global object (window in browsers, global in Node.js) cast to a specific type.
 *
 * @template TArgs - The type definition for custom properties expected on the global object
 * @returns {Partial<TArgs>} The global object cast as a partial of the specified type
 *
 * @remarks
 * This utility provides type-safe access to the global object with custom properties.
 * It uses type assertions to convert globalThis into a typed structure, making all
 * properties optional via Partial. This is useful when working with third-party scripts
 * that attach properties to the global scope.
 *
 * @example
 * ```typescript
 * // Define expected global properties
 * interface GlobalAnalytics {
 *   gtag: (command: string, ...args: any[]) => void;
 *   dataLayer: unknown[];
 * }
 *
 * // Retrieve typed global object
 * const globals = fromGlobalThis<GlobalAnalytics>();
 *
 * // Safe access with optional chaining
 * globals.gtag?.('event', 'page_view');
 * globals.dataLayer?.push({ event: 'custom' });
 * ```
 *
 * @example
 * ```typescript
 * // Use case: Accessing Sitecore Engage SDK
 * interface SitecoreGlobals {
 *   engage: Engage;
 *   cdpClientKey: string;
 * }
 *
 * const sitecore = fromGlobalThis<SitecoreGlobals>();
 * if (sitecore.engage) {
 *   await sitecore.engage.pageView({ channel: 'WEB', currency: 'USD' });
 * }
 * ```
 */
export const fromGlobalThis = <TArgs>(): Partial<TArgs> =>
  globalThis as GlobalWithArgs<TArgs> as Partial<TArgs>;

/**
 * Assigns properties from an object to the global object (window in browsers, global in Node.js).
 *
 * @template TArgs - The type definition for properties to be assigned to the global object
 * @param {Partial<TArgs>} obj - The object containing properties to assign to globalThis
 * @param {boolean} [clearUnknown=false] - If true, assigns all properties including undefined/null values; if false, only assigns truthy values
 * @returns {Partial<TArgs>} The global object cast as a partial of the specified type
 *
 * @remarks
 * This utility merges properties from a provided object into the global scope. By default,
 * it only assigns truthy values to prevent polluting the global namespace with undefined
 * or null properties. Set `clearUnknown` to true to explicitly assign all values including
 * falsy ones. Useful for exposing APIs or configuration to the global scope for third-party
 * script integration.
 *
 * @example
 * ```typescript
 * // Define global API interface
 * interface MyGlobalAPI {
 *   analytics: Analytics;
 *   config: Config;
 *   version: string;
 * }
 *
 * // Expose API to global scope
 * toGlobalThis<MyGlobalAPI>({
 *   analytics: new Analytics(),
 *   config: { apiKey: 'abc123' },
 *   version: '1.0.0'
 * });
 *
 * // Now accessible globally
 * // window.analytics.track('event');
 * ```
 *
 * @example
 * ```typescript
 * // Use case: Clear specific global values
 * interface GlobalFlags {
 *   debugMode: boolean;
 *   testMode: boolean;
 * }
 *
 * // Explicitly clear values (set to undefined)
 * toGlobalThis<GlobalFlags>({
 *   debugMode: undefined,
 *   testMode: undefined
 * }, true); // clearUnknown = true to assign undefined values
 * ```
 *
 * @example
 * ```typescript
 * // Use case: Conditional assignment
 * interface AppGlobals {
 *   engage?: Engage;
 *   gtag?: Function;
 * }
 *
 * // Only assigns non-null/undefined values
 * toGlobalThis<AppGlobals>({
 *   engage: engageInstance,
 *   gtag: undefined // Won't be assigned since clearUnknown is false
 * });
 * ```
 */
export const toGlobalThis = <TArgs>(obj: Partial<TArgs>, clearUnknown = false): Partial<TArgs> =>
  Object.entries(obj).reduce((globalObj, [key, value]) => {
    const target = globalObj;
    if (clearUnknown || (value !== null && value !== undefined)) {
      target[key as keyof TArgs] = value as TArgs[keyof TArgs];
    }
    return globalThis as GlobalWithArgs<TArgs> as Partial<TArgs>;
  }, globalThis as GlobalWithArgs<TArgs> as Partial<TArgs>);
